<script setup lang="ts">
  import { computed } from 'vue';
  import { DefaultTable, SearchNoResults, PaginateOrScroll, TableHeaderSection } from '@/shared/ui';
  import type { TrainingContentListItem, TrainingContentListMetadata } from '@/entities/Training';
  import {
    CONTENT_ORDER_OPTIONS,
    CONTENT_TABLE_COLUMN_WIDTH_CLASSES,
    contentTableCellModifierClass,
  } from '../model/useTrainingContentsTable';
  import TrainingContentsTableRow from './TrainingContentsTableRow.vue';

  const TABLE_HEADERS = ['Título', 'Data criação', 'Extensão', 'Tipo de conteúdo', 'Ações'];

  const props = withDefaults(
    defineProps<{
      contents: TrainingContentListItem[];
      metadata: TrainingContentListMetadata | null;
      itemsPerPage: number;
      currentSort?: string;
      currentOrder?: string;
      loadingMore?: boolean;
      hasMoreItems?: boolean;
      isFilterActive?: boolean;
    }>(),
    {
      currentSort: '',
      currentOrder: '',
      loadingMore: false,
      hasMoreItems: false,
      isFilterActive: false,
    }
  );

  const emit = defineEmits<{
    'change-page': [page: number];
    'update:sort': [sort: string, order: string];
    'filter-clicked': [];
    view: [content: TrainingContentListItem];
    edit: [content: TrainingContentListItem];
    delete: [content: TrainingContentListItem];
  }>();

  const isEmpty = computed(() => props.contents.length === 0);

  function contentThClass(_header: string, index: number) {
    return [
      'text-left text-xs font-semibold text-gray-07 pl-6 py-4.5 align-middle',
      contentTableCellModifierClass(index),
    ]
      .filter(Boolean)
      .join(' ');
  }
</script>

<template>
  <PaginateOrScroll
    data-testid="training-contents-paginate"
    :items="contents"
    :all-items="contents"
    :metadata="metadata"
    :selected-quantity="itemsPerPage"
    :loading-more="loadingMore"
    :has-more-items="hasMoreItems"
    entity-name="conteúdo"
    @page-changed="(newPage: number) => emit('change-page', newPage)"
  >
    <template #table="{ items: pageItems }">
      <DefaultTable table-class="w-full table-fixed divide-y divide-gray-02">
        <colgroup>
          <col
            v-for="widthClass in CONTENT_TABLE_COLUMN_WIDTH_CLASSES"
            :key="widthClass"
            :class="widthClass.split(' ')[0]"
          />
        </colgroup>
        <TableHeaderSection
          title="Lista de Conteúdos"
          :headers="TABLE_HEADERS"
          :has-selection="false"
          :has-selected="false"
          :selected-count="0"
          :selected-documents="[]"
          :all-selected="false"
          :item-count="pageItems.length"
          item-type="conteúdos"
          :order-options="CONTENT_ORDER_OPTIONS"
          :current-sort="currentSort"
          :current-order="currentOrder"
          :has-filter="true"
          :is-filter-active="isFilterActive"
          :has-delete="false"
          :show-column-headers="!isEmpty"
          :th-class="contentThClass"
          @update:sort="(sort: string, order: string) => emit('update:sort', sort, order)"
          @filter-clicked="emit('filter-clicked')"
        />
        <tbody class="border-t border-gray-02">
          <tr v-if="isEmpty">
            <td :colspan="TABLE_HEADERS.length" class="py-8">
              <SearchNoResults data-testid="training-contents-no-results-state" />
            </td>
          </tr>
          <TrainingContentsTableRow
            v-for="content in pageItems"
            :key="content.id"
            :content="content"
            @view="(viewed: TrainingContentListItem) => emit('view', viewed)"
            @edit="(edited: TrainingContentListItem) => emit('edit', edited)"
            @delete="(deleted: TrainingContentListItem) => emit('delete', deleted)"
          />
        </tbody>
      </DefaultTable>
    </template>
  </PaginateOrScroll>
</template>
